import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { TransferInventoryDto } from './inventory.model';

interface StockLedgerReportPayload {
  fromDate?: string;
  toDate?: string;
  reportType: 'Daily' | 'Monthly' | 'Yearly';
  /** Restrict to these stores/warehouses; omitted → all locations merged. */
  storeIds?: string[];
}
@Injectable({
  providedIn: 'root',
})
export class InventoryService {
  constructor(private http: HttpClient) {}
  API_URL = environment.apiUrl;

  addInventory(dto: any): Observable<any> {
    return this.http.post(environment.inventory, dto);
  }

  getAllInventory(params: { page: number; limit: number }): Observable<any> {
    return this.http.get<any>(
      `${environment.inventory}?limit=${params.limit}&page=${params.page}`
    );
  }

  getInventoryById(id: string): Observable<any> {
    return this.http.get(`${environment.inventory}/${id}`);
  }

  getInventoryByCode(code: string, isActive: boolean): Observable<any> {
    const params = new HttpParams().set('isActive', isActive);
    return this.http.get(`${environment.inventory}/code/${code}`, { params });
  }

  getInventoryByProduct(productId: string): Observable<any> {
    return this.http.get(`${environment.inventory}/product/${productId}`);
  }

  /** Sellable / in-transit on a location plus what each sub-area (damage area…) holds. */
  getLocationStockSummary(locationId: string): Observable<any> {
    return this.http.get(`${environment.inventory}/location-summary/${locationId}`);
  }

  getInventoryByLocation(locationId: string): Observable<any> {
    return this.http.get(`${environment.inventory}/location/${locationId}`);
  }

  getInventoryByQueryAndLocation(params: {
    locationId: string;
    query?: string;
    page?: number;
    limit?: number;
    context?: string;
  }): Observable<any> {
    let httpParams = new HttpParams();

    // Set the context
    if (params.context) {
      httpParams = httpParams.set('context', params.context);
    }

    if (params.query) {
      httpParams = httpParams.set('query', params.query);
    }

    httpParams = httpParams.set('locationId', params.locationId);

    if (params.page) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.limit) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }

    return this.http.get(`${environment.inventory}/search`, {
      params: httpParams,
    });
  }

  adjustQuantity(code: string, id: string, payload: any): Observable<any> {
    return this.http.patch(
      `${environment.inventory}/${code}/${id}/adjust`,
      payload
    );
  }

  deleteInventoryById(id: string): Observable<any> {
    return this.http.delete(`${environment.inventory}/${id}`);
  }

  deleteInventoryByCodeAndId(code: string, id: string): Observable<any> {
    return this.http.delete(`${environment.inventory}/${code}/${id}`);
  }

  getAllLocations(): Observable<any> {
    const params = new HttpParams().set('isActive', true);
    return this.http.get(environment.locations, { params });
  }

  getAllStores() {
    return this.http.get(this.API_URL + '/stores');
  }

  addInventoryToStore(payload: any): Observable<any> {
    return this.http.post(`${environment.stores}/add-inventory`, payload);
  }

  getStoreInventories(storeCode: string): Observable<any> {
    return this.http.get(`${environment.stores}/${storeCode}/inventories`);
  }

  removeInventoryFromStore(
    storeCode: string,
    inventoryId: string
  ): Observable<any> {
    return this.http.delete(
      `${environment.stores}/${storeCode}/inventories/${inventoryId}`
    );
  }

  transferStock(payload: TransferInventoryDto): Observable<any> {
    return this.http.post(environment.stockTransfer, payload);
  }

  getLowStockProducts(locationId: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}/inventory/low-stock`, {
      params: { locationId },
    });
  }

  createReorderRequest(payload: any): Observable<any> {
    return this.http.post(`${environment.apiUrl}/inventory/reorder`, payload);
  }

  getAllReorderRequests(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/inventory/reorder`);
  }
  exportStockLedgerReport(payload: StockLedgerReportPayload): Observable<Blob> {
    const url = `${this.API_URL}/inventory/report/stock-ledger`;

    return this.http.post(url, payload, {
      responseType: 'blob',
    });
  }

  exportStockStatus(payload: {
    locationId: string;
    query?: string;
  }): Observable<Blob> {
    return this.http.post(
      `${environment.inventory}/stock-status/export-xlsx`,
      payload,
      { responseType: 'blob' }
    );
  }
}
